import { faCheck, faCircleNotch, faExclamationCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, DialogTitle, Alert, Grid, IconButton, Typography } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DialogActions from "src/containers/DialogActions/DialogActions";
import { CompanyAccountRef } from "src/models/company-accounts/company-accounts";
import { CustomDate } from "src/models/custom-date/custom-date";
import { EmployeeRef } from "src/models/employees";
import { TeamMemberSTO } from "src/models/team-member/team-member-sto";
import { UserRef } from "src/models/user";
import { UsersApiService } from "src/services/users";
import { v4 as uuid } from "uuid";
import * as XLSX from "xlsx";
import AddEditTeamMember from "./AddEditTeamMember/AddEditTeamMember";
import LocalFilePicker from "./LocalFilePicker/LocalFilePicker";
import SortingCell from "./SortingCell/SortingCell";
import { Container } from "./styles";
import TeamMemberCard from "./TeamMemberCard/TeamMemberCard";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import TableHeader from "../../../containers/TableHeader/TableHeader";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useSearchOptions from "../../../custom-hooks/useSearchOptions/useSearchOptions";
import { Page } from "../../../models/api/page";
import { PropsFilter } from "../../../models/api/props-filter";
import { ListSortDirection } from "../../../models/api/sort";
import { CompanyLegalEntityDTO } from "../../../models/company-accounts/company-legal-entities";
import { TeamMemberDTO } from "../../../models/team-member/team-member-dto";
import { NotAuthorizedPage } from "../../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import CustomTablePagination from "../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { CompanyLegalEntitiesApiService } from "../../../services/company-legal-entities";
import { TeamMembersApiService } from "../../../services/team-members/team-members-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

interface MemberStatus {
  status: "pending" | "saving" | "saved" | "error" | "missing-data";
  message?: string;
}

interface State {
  loading: boolean;
  error: string;
  items: TeamMemberDTO[];
  totalItems: number;
  page: Page;
  isPreviewDialogOpen: boolean;
  // legalEntity: CompanyLegalEntityRef | null;
  parsedMembers: TeamMemberSTO[];
  memberStatuses: Map<string, MemberStatus>;
  isSaving: boolean;
  authorizedLE: CompanyLegalEntityDTO | null;
  authorizedLELoading: boolean;
  sortModel: GridSortModel;
}

const MyTeamMembers = () => {
  const { currentUserAuthorizations, currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);
  const isAdmin = currentUserAuthorizations?.canChangeAll || currentUserAuthorizations?.canViewAll;
  const isManager = useAuthorization([AUTHORIZATIONS.LegalEntityAccess.SimplifiedTeamManagement]);

  const hasAccess = isAdmin || isManager;

  const tRP = "components.team_members";
  const { t } = useTranslation();

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    totalItems: 0,
    page: { pageNumber: 0, pageSize: 10 },
    isPreviewDialogOpen: false,
    // legalEntity: null,
    parsedMembers: [],
    memberStatuses: new Map(),
    isSaving: false,
    authorizedLE: null,
    authorizedLELoading: false,
    sortModel: [
      {
        field: "name",
        sort: "asc",
      },
    ],
  });

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions("");

  // #region handleChangeSortModel
  const handleChangeSortModel = (model: GridSortModel) => {
    if (!isEqual(state.sortModel, model) && model.length)
      setState((state) => ({
        ...state,
        sortModel: model,
      }));
  };
  // #endregion

  // #region loadItems
  const loadItems = useCallback(
    async (signal?: AbortSignal) => {
      setState((previousState) => ({ ...previousState, loading: true }));
      try {
        const { field, sort } = state.sortModel[0] || { field: "", sort: "asc" };
        const { Ascending, Descending } = ListSortDirection;

        const response = await new TeamMembersApiService().loadItems(
          {},
          searchOptions.query,
          !field
            ? undefined
            : [
                {
                  name: field,
                  direction: sort === "asc" ? Ascending : Descending,
                },
              ],
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );
        if (response.error) {
          setState((previousState) => ({ ...previousState, error: response.error }));
        } else {
          setState((previousState) => ({
            ...previousState,
            items: response.items,
            error: "",
            totalItems: response.totalItems,
          }));
        }
      } catch (error) {
        setState((previousState) => ({ ...previousState, error: error as string }));
      } finally {
        setState((previousState) => ({ ...previousState, loading: false }));
      }
    },
    [searchOptions, state.page, state.sortModel]
  );
  // #endregion

  // #region reload
  const reload = useCallback(() => {
    if (state.page.pageNumber === 0) {
      loadItems();
    } else {
      setState((previousState) => ({
        ...previousState,
        page: { ...state.page, pageNumber: 0 },
      }));
    }
  }, [state.page, loadItems]);
  // #endregion

  // #region getAuthorizedLegalEntity
  const getLegalEntity = useCallback(async () => {
    setState((previousState) => ({ ...previousState, authorizedLELoading: true }));
    try {
      const response = await new CompanyLegalEntitiesApiService().loadItems({
        contextAuthorizations: [AUTHORIZATIONS.LegalEntityAccess.SimplifiedTeamManagement],
      } as PropsFilter);
      if (response.error) {
        console.log(response.error);
      } else {
        const legalEntity = response.items[0];
        setState((previousState) => ({
          ...previousState,
          authorizedLE: legalEntity,
        }));
      }
    } catch (error) {
      console.log(error as string);
    } finally {
      setState((previousState) => ({ ...previousState, authorizedLELoading: false }));
    }
  }, []);
  // #endregion

  useEffect(() => {
    getLegalEntity();
  }, []);

  useEffect(() => {
    loadItems();
  }, [state.page, state.sortModel]);

  useEffect(() => {
    reload();
  }, [searchOptions]);

  // #region change page size
  const handleChangePageSize = useCallback(
    (pageSize: number) => {
      if (state.page.pageNumber === 0) {
        // loadItems();
        setState((previousState) => ({
          ...previousState,
          page: {
            ...previousState.page,
            pageSize,
          },
        }));
      } else {
        setState((previousState) => ({
          ...previousState,
          page: {
            pageSize,
            pageNumber: 0,
          },
        }));
      }
    },
    [state.page.pageNumber]
  );
  // #endregion

  // #region change page number
  const handleChangePageNumber = useCallback((pageNumber: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        pageNumber,
      },
    }));
  }, []);
  // #endregion

  // #region handle close preview dialog
  const handleClosePreviewDialog = useCallback(() => {
    const hasSavedMembers = Array.from(state.memberStatuses.values()).some((status) => status.status === "saved");

    setState((prevState) => ({
      ...prevState,
      isPreviewDialogOpen: false,
      parsedMembers: [],
      memberStatuses: new Map(),
      isSaving: false,
    }));

    if (hasSavedMembers) {
      reload();
    }
  }, [state.memberStatuses, reload]);
  // #endregion

  // #region parse date from Excel
  const parseExcelDate = useCallback((value: any): CustomDate | null => {
    if (!value) return null;

    try {
      if (typeof value === "number") {
        const date = XLSX.SSF.parse_date_code(value);
        return CustomDate.FromDate(new Date(date.y, date.m - 1, date.d));
      }

      if (typeof value === "string") {
        const parts = value.split("/");
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          return CustomDate.FromDate(new Date(year, month - 1, day));
        }
      }

      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return CustomDate.FromDate(date);
      }
    } catch (error) {
      console.error("Error parsing date:", error);
    }

    return null;
  }, []);
  // #endregion

  // #region validate team member
  const validateTeamMember = useCallback((member: TeamMemberSTO): { valid: boolean; message?: string } => {
    const missingFields: string[] = [];

    if (!member.employee?.firstName) missingFields.push("First Name");
    if (!member.employee?.lastName) missingFields.push("Last Name");
    if (!member.employee?.email) missingFields.push("Email");
    if (!member.startDate) missingFields.push("Start Date");
    if (!member.approvers || member.approvers.length === 0) missingFields.push("At least one Approver");

    if (missingFields.length > 0) {
      return {
        valid: false,
        message: `Missing required fields: ${missingFields.join(", ")}`,
      };
    }

    return { valid: true };
  }, []);
  // #endregion

  // #region check user exists by email
  const checkUserExistsByEmail = useCallback(async (email: string): Promise<UserRef | null> => {
    try {
      const response = await new UsersApiService().loadItems(
        {} as PropsFilter,
        email,
        undefined,
        { pageNumber: 0, pageSize: 1 },
        true
      );
      if (response.error || response.items.length === 0) {
        return null;
      }
      return response.items[0] as UserRef;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return null;
    }
  }, []);
  // #endregion

  // #region handle local file selected
  const handleLocalFileSelected = useCallback(
    async (file: File) => {
      setState((prevState) => ({ ...prevState, loading: true }));

      try {
        if (!state.authorizedLE) {
          await getLegalEntity();
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];

        const dataRows = jsonData.slice(1).filter((row) => row.length > 0);

        if (dataRows.length === 0) {
          toast.warning(t(`${tRP}.no_valid_rows_found`));
          setState((prevState) => ({ ...prevState, loading: false }));
          return;
        }

        interface TeamMemberData {
          firstName: string;
          lastName: string;
          startDate: CustomDate | null;
          endDate: CustomDate | null;
          employeeCode: string;
          employeeEmail: string;
          approvers: Array<{ firstName: string; lastName: string; email: string }>;
        }

        const teamMembersData: TeamMemberData[] = [];
        let currentMember: TeamMemberData | null = null;

        for (const row of dataRows) {
          if (row[0]) {
            if (currentMember) {
              teamMembersData.push({ ...currentMember, approvers: [...currentMember.approvers] });
            }

            currentMember = {
              firstName: String(row[0] || ""),
              lastName: String(row[1] || ""),
              startDate: parseExcelDate(row[2]),
              endDate: parseExcelDate(row[3]),
              employeeCode: row[4] ? String(row[4]) : "",
              employeeEmail: String(row[8] || ""),
              approvers: [],
            };

            const approverEmail = row[7];
            if (approverEmail) {
              currentMember.approvers.push({
                firstName: String(row[5] || ""),
                lastName: String(row[6] || ""),
                email: String(approverEmail),
              });
            }
          } else if (currentMember && (row[5] || row[6] || row[7])) {
            const approverEmail = row[7];
            if (approverEmail) {
              currentMember.approvers.push({
                firstName: String(row[5] || ""),
                lastName: String(row[6] || ""),
                email: String(approverEmail),
              });
            }
          }
        }

        if (currentMember) {
          teamMembersData.push({ ...currentMember, approvers: [...currentMember.approvers] });
        }

        const teamMembers: TeamMemberSTO[] = [];

        for (const memberData of teamMembersData) {
          const { firstName, lastName, startDate, endDate, employeeCode, employeeEmail, approvers } = memberData;

          if (!firstName || !lastName || !startDate || !employeeEmail) {
            continue;
          }

          if (approvers.length === 0) {
            continue;
          }

          const teamMember = {
            id: uuid(),
            isDeleted: false,
            companyAccount: currentCompanyAccount as CompanyAccountRef,
            legalEntity: state.authorizedLE,
            employee: {
              id: uuid(),
              isDeleted: false,
              firstName,
              lastName,
              name: `${firstName} ${lastName}`,
              email: employeeEmail,
              employeeCode,
              legalEntity: state.authorizedLE,
              companyAccount: currentCompanyAccount as CompanyAccountRef,
            } as EmployeeRef,
            startDate,
            endDate,
            approvers: approvers.map((approver) => ({
              id: uuid(),
              firstName: approver.firstName,
              lastName: approver.lastName,
              email: approver.email,
            })) as any,
          } as TeamMemberSTO;

          teamMembers.push(teamMember);
        }

        if (teamMembers.length > 0) {
          const statuses = new Map<string, MemberStatus>();

          const emailCounts = new Map<string, number>();
          teamMembers.forEach((member) => {
            const email = member.employee?.email?.toLowerCase();
            if (email) {
              emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
            }
          });

          const duplicateEmails = Array.from(emailCounts.entries())
            .filter(([_, count]) => count > 1)
            .map(([email]) => email);

          teamMembers.forEach((member) => {
            const validation = validateTeamMember(member);
            const email = member.employee?.email?.toLowerCase();

            if (!validation.valid) {
              statuses.set(member.id, {
                status: "missing-data",
                message: validation.message,
              });
            } else if (email && duplicateEmails.includes(email)) {
              statuses.set(member.id, {
                status: "error",
                message: `Duplicate email: ${member.employee?.email}`,
              });
            } else {
              statuses.set(member.id, { status: "pending" });
            }
          });

          if (duplicateEmails.length > 0) {
            toast.error(t(`${tRP}.duplicate_emails_found`, { count: duplicateEmails.length }));
          }

          setState((prevState) => ({
            ...prevState,
            parsedMembers: teamMembers,
            memberStatuses: statuses,
            isPreviewDialogOpen: true,
            loading: false,
          }));
        } else {
          toast.warning(t(`${tRP}.no_valid_rows_found`));
          setState((prevState) => ({ ...prevState, loading: false }));
        }
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        toast.error(t(`${tRP}.error_loading_file`));
        setState((prevState) => ({ ...prevState, loading: false }));
      }
    },
    [state.authorizedLE, getLegalEntity, parseExcelDate, currentCompanyAccount, validateTeamMember]
  );
  // #endregion

  // #region save imported team members
  const handleSaveImportedMembers = useCallback(async () => {
    if (state.parsedMembers.length === 0) return;

    setState((prevState) => ({ ...prevState, isSaving: true }));

    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    for (const member of state.parsedMembers) {
      const currentStatus = state.memberStatuses.get(member.id);

      if (currentStatus?.status === "missing-data" || currentStatus?.status === "error") {
        skippedCount++;
        continue;
      }

      if (currentStatus?.status === "saved") {
        continue;
      }

      setState((prevState) => {
        const newStatuses = new Map(prevState.memberStatuses);
        newStatuses.set(member.id, { status: "saving" });
        return { ...prevState, memberStatuses: newStatuses };
      });

      try {
        const approversWithExistingUsers = await Promise.all(
          (member.approvers || []).map(async (approver: any) => {
            if (!approver.email) return approver;

            const existingUser = await checkUserExistsByEmail(approver.email);
            if (existingUser) {
              return existingUser;
            }
            return approver;
          })
        );

        const memberToSave = {
          ...member,
          approvers: approversWithExistingUsers,
        };

        const response = await new TeamMembersApiService().saveItems([memberToSave]);

        if (response.error) {
          setState((prevState) => {
            const newStatuses = new Map(prevState.memberStatuses);
            newStatuses.set(member.id, {
              status: "error",
              message: response.error,
            });
            return { ...prevState, memberStatuses: newStatuses };
          });
          errorCount++;
        } else {
          setState((prevState) => {
            const newStatuses = new Map(prevState.memberStatuses);
            newStatuses.set(member.id, { status: "saved" });
            return { ...prevState, memberStatuses: newStatuses };
          });
          successCount++;
        }
      } catch (error) {
        setState((prevState) => {
          const newStatuses = new Map(prevState.memberStatuses);
          newStatuses.set(member.id, {
            status: "error",
            message: error as string,
          });
          return { ...prevState, memberStatuses: newStatuses };
        });
        errorCount++;
      }
    }

    setState((prevState) => ({ ...prevState, isSaving: false }));

    if (successCount > 0) {
      toast.success(t(`${tRP}.success_importing_members`, { count: successCount }));
    }
    if (errorCount > 0) {
      toast.error(t(`${tRP}.error_importing_some_members`, { count: errorCount }));
    }
    if (skippedCount > 0) {
      toast.error(t(`${tRP}.skipped_members`, { count: skippedCount }));
    }
  }, [state.parsedMembers, state.memberStatuses, checkUserExistsByEmail, state.authorizedLE]);
  // #endregion

  // #region load legal entity on mount
  useEffect(() => {
    getLegalEntity();
  }, []);
  // #endregion

  // #region check if all saveable members are saved
  const allSaveableMembersSaved = useMemo(() => {
    if (state.parsedMembers.length === 0) return false;

    let hasSaveableMembers = false;
    for (const member of state.parsedMembers) {
      const status = state.memberStatuses.get(member.id);
      if (status?.status === "missing-data") {
        continue;
      }
      hasSaveableMembers = true;
      if (status?.status !== "saved") {
        return false;
      }
    }

    return hasSaveableMembers;
  }, [state.parsedMembers, state.memberStatuses]);
  // #endregion

  return (
    <>
      {hasAccess ? (
        <>
          <TableHeader
            tableTitle={`${t(`${tRP}.title`)}`}
            searchOptions={searchOptions}
            handleChangeSearchOptions={handleChangeSearchOptions}
            buttonToRender={
              state.authorizedLE ? (
                <Grid style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <AddEditTeamMember handleNotifyParent={reload} />
                  <LocalFilePicker
                    onFileSelected={handleLocalFileSelected}
                    acceptedExtensions=".xlsx,.xls"
                    title={t(`${tRP}.import_excel_file`)}
                  />
                </Grid>
              ) : state.authorizedLELoading ? (
                <></>
              ) : (
                <Alert severity="warning">{`${t(`${tRP}.missing_entity_warning`)}`}</Alert>
              )
            }
            quickFilters={
              <Grid
                style={{
                  display: "flex",
                  gap: "20px",
                  padding: "16px",
                  alignItems: "center",
                  // backgroundColor: theme.palette.background.paper,
                  borderRadius: "4px",
                  width: "fit-content",
                }}
              >
                <Typography>{t(`${tRP}.sort_by`)}</Typography>
                <SortingCell
                  title="Name"
                  fieldName="name"
                  handleChangeSortModel={handleChangeSortModel}
                  sortModel={state.sortModel}
                />
                <SortingCell
                  title="Start Date"
                  fieldName="employmentContract.StartDate"
                  handleChangeSortModel={handleChangeSortModel}
                  sortModel={state.sortModel}
                />
                <SortingCell
                  title="End Date"
                  fieldName="employmentContract.EndDate"
                  handleChangeSortModel={handleChangeSortModel}
                  sortModel={state.sortModel}
                />
              </Grid>
            }
          />

          <Dialog
            open={state.isPreviewDialogOpen}
            onClose={handleClosePreviewDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Grid style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">
                  {t(`${tRP}.preview_import`)} ({state.parsedMembers.length})
                </Typography>
                <IconButton onClick={handleClosePreviewDialog}>
                  <FontAwesomeIcon icon={faTimes} />
                </IconButton>
              </Grid>
            </DialogTitle>
            <DialogContent>
              {state.parsedMembers.map((member, idx) => {
                const status = state.memberStatuses.get(member.id);
                return (
                  <Grid
                    key={idx}
                    style={{ display: "flex", alignItems: "center", gap: "16px" }}
                  >
                    <Grid style={{ flex: 1 }}>
                      <TeamMemberCard
                        item={member as any}
                        handleNotifyParent={() => {}}
                      />
                    </Grid>
                    <Grid
                      style={{
                        minWidth: "40px",
                        width: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {status?.status === "saving" && (
                        <FontAwesomeIcon
                          icon={faCircleNotch}
                          spin
                          color="#1976d2"
                          size="2xl"
                          title="Saving..."
                        />
                      )}
                      {status?.status === "saved" && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#4caf50"
                          size="2xl"
                          title="Saved"
                        />
                      )}
                      {status?.status === "error" && (
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          color="#f44336"
                          size="2xl"
                          title={`Error: ${status.message}`}
                        />
                      )}
                      {status?.status === "missing-data" && (
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          color="#ff9800"
                          size="2xl"
                          title={status.message}
                        />
                      )}
                    </Grid>
                  </Grid>
                );
              })}
            </DialogContent>
            <DialogActions
              handleCancel={handleClosePreviewDialog}
              handleSave={allSaveableMembersSaved ? handleClosePreviewDialog : handleSaveImportedMembers}
              disabledSaveButton={state.isSaving}
              disabledCancelButton={state.isSaving}
              customSaveName={
                allSaveableMembersSaved
                  ? t(`buttons.close`)
                  : state.isSaving
                  ? t(`common_words.saving`)
                  : t(`buttons.save`)
              }
            />
          </Dialog>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Container>
              {state.items.length === 0 ? (
                <Grid
                  style={{
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Typography>{`${t(`common_words.no_items`)}`}</Typography>
                </Grid>
              ) : (
                state.items.map((_item, idx) => (
                  <AddEditTeamMember
                    key={idx}
                    item={_item}
                    handleNotifyParent={reload}
                  >
                    <TeamMemberCard
                      item={_item}
                      handleNotifyParent={reload}
                    />
                  </AddEditTeamMember>
                ))
              )}
            </Container>
          </LoadingOverlay>
          <CustomTablePagination
            pageNumber={state.page.pageNumber}
            pageSize={state.page.pageSize}
            handleChangePageNumber={handleChangePageNumber}
            handleChangePageSize={handleChangePageSize}
            totalItems={state.totalItems}
          />
        </>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};

export default MyTeamMembers;
